import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { HiMenu, HiX, HiChevronDown, HiUser, HiLogout, HiCheckCircle } from 'react-icons/hi';
import { useAuth } from '../../contexts/AuthContext';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const location = useLocation();
  const isNotHome = location.pathname !== '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = () => setShowUserMenu(false);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleScrollTo = (sectionId) => {
    setIsOpen(false);
    setShowDropdown(false);
    setShowUserMenu(false);

    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) section.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    } else {
      const section = document.getElementById(sectionId);
      if (section) section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLogout = async () => {
    setShowUserMenu(false);
    await logout();
    navigate('/');
    setIsOpen(false);
  };

  const handleGoToDashboard = () => {
    setShowUserMenu(false);
    const role = user?.type || user?.role;

    switch (role) {
      case 'admin':
        navigate('/admin');
        break;
      case 'parent':
        navigate('/parent');
        break;
      case 'apprenant':
        navigate('/apprenant');
        break;
      case 'formateur':
        navigate('/formateur');
        break;
      default:
        navigate('/');
    }
  };

  const isDarkText = scrolled || isOpen || isNotHome;

  const getInitials = () => {
    const nom = user?.nom || user?.name || user?.username || '';
    const parts = nom.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return nom.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = () => {
    const colors = [
      'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500',
      'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500',
      'bg-orange-500', 'bg-cyan-500'
    ];
    const nom = user?.nom || user?.name || user?.username || '';
    const index = nom.length % colors.length;
    return colors[index];
  };

  const userName = user?.nom || user?.name || user?.username || 'Connecté';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || isOpen || isNotHome ? 'bg-white shadow-md' : 'bg-transparent'}`}>
      <div className="flex items-center justify-between px-6 md:px-8 py-3">

        {/* LOGO */}
        <Link to="/" onClick={() => setIsOpen(false)} className="flex-shrink-0 z-50">
          <img src="/assets/logo-removebg-preview (1).png" alt="Pschool Logo" className="h-10 md:h-12" />
        </Link>

        {/* MENU DESKTOP */}
        <div className="hidden md:flex items-center space-x-6 font-medium">
          <Link to="/" className={`transition hover:text-orange-500 ${isDarkText ? 'text-gray-700' : 'text-white'}`}>Accueil</Link>
          <Link to="/a-propos" className={`transition hover:text-orange-500 ${isDarkText ? 'text-gray-700' : 'text-white'}`}>À propos</Link>
          <Link to="/services" className={`transition hover:text-orange-500 ${isDarkText ? 'text-gray-700' : 'text-white'}`}>Nos Services</Link>

          {/* DROPDOWN */}
          <div className="relative" onMouseEnter={() => setShowDropdown(true)} onMouseLeave={() => setShowDropdown(false)}>
            <button className={`flex items-center gap-1 transition hover:text-orange-500 ${isDarkText ? 'text-gray-700' : 'text-white'}`}>
              Formations <HiChevronDown className={`w-4 h-4 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showDropdown && (
              <div className="absolute top-full left-0 w-56 bg-white shadow-lg rounded-md py-2 border border-gray-100">
                <Link to="/elearning" className="block px-4 py-2 hover:bg-orange-50 hover:text-orange-500 transition">E-learning</Link>
                <Link to="/formationSessions" className="block px-4 py-2 hover:bg-orange-50 hover:text-orange-500 transition">Sessions Programmées</Link>
              </div>
            )}
          </div>
          <Link to="/evenements" className={`transition hover:text-orange-500 ${isDarkText ? 'text-gray-700' : 'text-white'}`}>Évènements</Link>
          <button onClick={() => handleScrollTo('temoignages')} className={`transition hover:text-orange-500 ${isDarkText ? 'text-gray-700' : 'text-white'}`}>Témoignages</button>
          <button onClick={() => handleScrollTo('faq')} className={`transition hover:text-orange-500 ${isDarkText ? 'text-gray-700' : 'text-white'}`}>FAQ</button>
          <button onClick={() => handleScrollTo('contact')} className={`transition hover:text-orange-500 ${isDarkText ? 'text-gray-700' : 'text-white'}`}>Contact</button>
        </div>

        {/* ACTIONS */}
        <div className="flex items-center z-50 space-x-4">
          {user ? (
            <div className="flex items-center gap-3">
              {/* Badge "Vous êtes connecté" - sans email */}
              <span className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 border border-green-500 rounded-full text-xs text-green-700">
                <HiCheckCircle className="w-3.5 h-3.5 text-green-500" />
                Vous êtes connecté
              </span>

              {/* Avatar avec menu déroulant */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowUserMenu(!showUserMenu);
                  }}
                  className={`w-10 h-10 rounded-full ${getAvatarColor()} text-white font-bold text-sm flex items-center justify-center hover:ring-2 hover:ring-offset-2 hover:ring-green-500 transition-all`}
                >
                  {getInitials()}
                </button>

                {/* Menu déroulant - sans email */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-2 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-800">{userName}</p>
                    </div>
                    <button
                      onClick={handleGoToDashboard}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition flex items-center gap-3"
                    >
                      <HiUser className="w-4 h-4 text-gray-400" />
                      Voir mon espace
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 transition flex items-center gap-3"
                    >
                      <HiLogout className="w-4 h-4 text-red-400" />
                      Me déconnecter
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <Link to="/login" className="px-5 py-2 bg-orange-600 text-white rounded-md font-medium hover:bg-orange-700 transition text-sm">
              Connexion
            </Link>
          )}

          {/* Menu Hamburger */}
          <button onClick={() => setIsOpen(!isOpen)} className={`md:hidden p-2 ${isDarkText ? 'text-gray-800' : 'text-white'}`}>
            {isOpen ? <HiX className="text-2xl" /> : <HiMenu className="text-2xl" />}
          </button>
        </div>
      </div>

      {/* MENU MOBILE */}
      <div className={`md:hidden fixed inset-0 bg-white z-40 flex flex-col pt-20 pb-10 transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col items-center space-y-4 font-medium text-lg px-8 overflow-y-auto">
          <Link to="/" onClick={() => setIsOpen(false)} className="w-full py-2 border-b text-gray-800 hover:text-orange-500">Accueil</Link>
          <Link to="/a-propos" onClick={() => setIsOpen(false)} className="w-full py-2 border-b text-gray-800 hover:text-orange-500">À propos</Link>
          <Link to="/services" onClick={() => setIsOpen(false)} className="w-full py-2 border-b text-gray-800 hover:text-orange-500">Nos Services</Link>
          <Link to="/elearning" onClick={() => setIsOpen(false)} className="w-full py-2 border-b text-gray-800 hover:text-orange-500">E-learning</Link>
          <Link to="/formationSessions" onClick={() => setIsOpen(false)} className="w-full py-2 border-b text-gray-800 hover:text-orange-500">Sessions Programmées</Link>
          <Link to="/evenements" onClick={() => setIsOpen(false)} className="w-full py-2 border-b text-gray-800 hover:text-orange-500">Évènements</Link>
          <button onClick={() => handleScrollTo('temoignages')} className="w-full py-2 border-b text-left text-gray-800 hover:text-orange-500">Témoignages</button>
          <button onClick={() => handleScrollTo('faq')} className="w-full py-2 border-b text-left text-gray-800 hover:text-orange-500">FAQ</button>
          <button onClick={() => handleScrollTo('contact')} className="w-full py-2 border-b text-left text-gray-800 hover:text-orange-500">Contact</button>

          <div className="w-full h-px bg-gray-200 my-4"></div>

          {/* Boutons mobile */}
          {user ? (
            <div className="w-full space-y-3">
              <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-lg">
                <div className={`w-10 h-10 rounded-full ${getAvatarColor()} text-white font-bold text-sm flex items-center justify-center`}>
                  {getInitials()}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">{userName}</p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-1.5 text-xs text-green-700 font-medium py-1">
                <HiCheckCircle className="w-4 h-4 text-green-500" />
                Vous êtes connecté
              </div>
              <button
                onClick={handleGoToDashboard}
                className="w-full py-3 text-center bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition"
              >
                Voir mon espace
              </button>
              <button
                onClick={handleLogout}
                className="w-full py-3 text-center bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100 transition"
              >
                Me déconnecter
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              onClick={() => setIsOpen(false)}
              className="w-full py-3 text-center bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition"
            >
              Connexion
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;